import {
  pollConversation,
  restartConversation,
  sendCustomerTyping,
  sendMessage,
  upsertFeedback,
} from "@/services";
import { createTransport } from "@/transport";
import { trackEvent } from "@/tracking";
import { storage } from "@/utils";
import {
  ChatWidgetHandoffStatus,
  ChatWidgetPollResponse,
  ChatWidgetTurn,
  ChatWidgetTurnFeedback,
  FormInstance,
} from "@/types";
import { latestTimestamp, mergeTurns } from "./turns";

/** Leave live chat and surface an error after this many consecutive poll failures. */
const MAX_POLL_FAILURES = 5;

/**
 * How the widget polls for turns it did not send itself.
 * - `live`: an operator is on the conversation, so poll fast.
 * - `background`: the bot is answering, but an operator can take the thread over
 *   from the inbox (Pause chatbot, or just replying) without the widget being
 *   told. Poll slowly so the takeover still shows up.
 * - `off`: nothing to watch.
 */
type PollMode = "live" | "background" | "off";

const POLL_INTERVAL_MS: Record<Exclude<PollMode, "off">, number> = {
  live: 3000,
  background: 15000,
};

/** Matches the backend's CONVERSATION_RESUME_TTL. After this much silence the
 * poll endpoint always reports `bot`, so background polling is pointless. */
const CONVERSATION_RESUME_TTL_MS = 24 * 60 * 60 * 1000;

/** Send at most one customer-typing ping per this interval while typing. */
const TYPING_THROTTLE_MS = 2000;

/** Notices raised while no UI is subscribed are queued for the next mount.
 * Bounded so a torn-down widget cannot grow the buffer without limit. */
const MAX_PENDING_NOTICES = 5;

export interface ChatSessionSeed {
  conversationHistory?: ChatWidgetTurn[];
  handoffStatus?: ChatWidgetHandoffStatus;
  forms?: FormInstance[];
}

export interface ChatSessionState {
  conversationHistory: ChatWidgetTurn[];
  hasRealConversation: boolean;
  isSendingMessage: boolean;
  forms: FormInstance[];
  activeFormKey: string | null;
  handoffStatus: ChatWidgetHandoffStatus;
  liveActive: boolean;
  agentTyping: boolean;
}

export interface ChatSessionNoticeListener {
  onError?: (message: string) => void;
  onSuccess?: (message: string) => void;
}

export interface ChatSession {
  readonly siteId: string;
  getState: () => ChatSessionState;
  subscribe: (listener: () => void) => () => void;
  addNoticeListener: (listener: ChatSessionNoticeListener) => () => void;
  sendMessage: (
    text: string,
    faqId?: number,
    welcomeButtonId?: number,
    quickReply?: boolean,
  ) => Promise<boolean>;
  restartSession: () => Promise<void>;
  openForm: (key: string) => void;
  closeForm: () => void;
  updateFormValues: (patch: Record<string, any>) => void;
  submitForm: () => Promise<boolean>;
  upsertTurnFeedback: (
    turnId: string,
    patch: {
      rating?: "positive" | "negative" | null;
      message?: string | null;
    },
  ) => Promise<boolean>;
  notifyTyping: () => void;
}

const sessions = new Map<string, ChatSession>();

function isLiveStatus(status: ChatWidgetHandoffStatus): boolean {
  return status === "waiting" || status === "live";
}

/** State to apply for a handoff status the server reported. */
function handoffState(
  status?: ChatWidgetHandoffStatus,
): Partial<ChatSessionState> {
  const live = status !== undefined && isLiveStatus(status);
  return status
    ? {
        handoffStatus: status,
        liveActive: live,
        ...(live ? {} : { agentTyping: false }),
      }
    : {};
}

function isPageVisible(): boolean {
  return (
    typeof document === "undefined" || document.visibilityState !== "hidden"
  );
}

function hasRecentTurn(turns: ChatWidgetTurn[], now: number): boolean {
  const latest = latestTimestamp(turns);
  return (
    latest !== undefined &&
    now - new Date(latest).getTime() < CONVERSATION_RESUME_TTL_MS
  );
}

function desiredPollMode(
  state: ChatSessionState,
  hasListeners: boolean,
  now: number,
): PollMode {
  let mode: PollMode = "off";
  if (hasListeners && state.liveActive) {
    mode = "live";
  } else if (
    hasListeners &&
    state.hasRealConversation &&
    isPageVisible() &&
    hasRecentTurn(state.conversationHistory, now)
  ) {
    mode = "background";
  }
  return mode;
}

/**
 * Module-level conversation store, keyed by site slug.
 *
 * Two React trees cannot share Context, and React 17 has no
 * `useSyncExternalStore`. Hooks subscribe with `useState`/`useEffect`.
 * One poller and one typing publisher live here so floating + embed mounts
 * share the same conversation without a second `/poll` loop.
 */

export function getChatSession(siteId: string): ChatSession | undefined {
  return sessions.get(siteId);
}

export function getOrCreateChatSession(
  siteId: string,
  seed: ChatSessionSeed = {},
): ChatSession {
  const existing = sessions.get(siteId);
  if (existing) {
    return existing;
  }
  const created = createChatSession(siteId, seed);
  sessions.set(siteId, created);
  return created;
}

function createChatSession(siteId: string, seed: ChatSessionSeed): ChatSession {
  const seedHistory = seed.conversationHistory ?? [];
  const seedHandoff = seed.handoffStatus ?? "bot";
  const seedForms = seed.forms ?? [];

  let state: ChatSessionState = {
    conversationHistory: seedHistory,
    hasRealConversation: seedHistory.length > 0,
    isSendingMessage: false,
    forms: seedForms,
    activeFormKey: null,
    handoffStatus: seedHandoff,
    liveActive: isLiveStatus(seedHandoff),
    agentTyping: false,
  };

  const listeners = new Set<() => void>();
  const noticeListeners = new Set<ChatSessionNoticeListener>();

  let pollsInFlight = 0;
  let pollFailureCount = 0;
  // Polls and sends can overlap. Rule: a poll that started before the latest
  // send, form submit or restart is discarded whole, and every send and form
  // submit ends with a fresh poll. Bumped when each of those starts.
  let epoch = 0;
  let lastTypingSent = 0;
  let transport: ReturnType<typeof createTransport> | null = null;
  let transportUnsubscribe: (() => void) | null = null;
  let pollMode: PollMode = "off";
  let formCloseTimer: ReturnType<typeof setTimeout> | null = null;

  const pendingNotices: Array<{
    kind: "error" | "success";
    message: string;
  }> = [];

  const notify = (
    listener: ChatSessionNoticeListener,
    kind: "error" | "success",
    message: string,
  ): void => {
    if (kind === "error") {
      listener.onError?.(message);
    } else {
      listener.onSuccess?.(message);
    }
  };

  const deliver = (kind: "error" | "success", message: string): void => {
    if (noticeListeners.size === 0) {
      pendingNotices.push({ kind, message });
      if (pendingNotices.length > MAX_PENDING_NOTICES) {
        pendingNotices.shift();
      }
      return;
    }
    noticeListeners.forEach((listener) => notify(listener, kind, message));
  };

  const emitError = (message: string): void => deliver("error", message);

  const emitSuccess = (message: string): void => deliver("success", message);

  const stopPoller = (): void => {
    transportUnsubscribe?.();
    transportUnsubscribe = null;
    if (transport) {
      transport.disconnect();
      transport = null;
    }
    pollMode = "off";
  };

  const startPoller = (mode: Exclude<PollMode, "off">): void => {
    transport = createTransport({ intervalMs: POLL_INTERVAL_MS[mode] });
    transportUnsubscribe = transport.subscribe((event) => {
      if (event.type === "changed") {
        void runPoll();
      }
    });
    transport.connect();
    pollMode = mode;
    // Count failures per mode: quiet background failures must not count
    // towards losing a live chat that has only just started.
    pollFailureCount = 0;
  };

  /** Start, stop or change the poller's cadence to match the current state. */
  const syncPoller = (): void => {
    const nextMode = desiredPollMode(state, listeners.size > 0, Date.now());
    if (nextMode !== pollMode) {
      stopPoller();
      if (nextMode !== "off") {
        startPoller(nextMode);
      }
      // Going live wants an operator's turns now. Background polling waits for
      // its first tick, so loading a page costs no extra request.
      if (nextMode === "live") {
        void runPoll();
      }
    }
  };

  // Hidden tabs stop background polling. On return, check at once: an operator
  // may have taken the conversation over while the visitor was away.
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", () => {
      const wasOff = pollMode === "off";
      syncPoller();
      if (wasOff && pollMode === "background") {
        void runPoll();
      }
    });
  }

  const setState = (
    updater: (prev: ChatSessionState) => ChatSessionState,
  ): void => {
    state = updater(state);
    listeners.forEach((listener) => listener());
    syncPoller();
  };

  /** Fetch turns and handoff status. A tick skips while another poll or a send
   * is out; `force` (the poll after a send) only waits for the send. */
  async function runPoll(force = false): Promise<void> {
    const userId = storage.getUserId(siteId);
    const conversationId = storage.getConversationId(siteId);
    // While a send is in flight the server already has the visitor's turn, so a
    // poll would add it next to the optimistic copy.
    const canPoll =
      Boolean(userId && conversationId) &&
      !state.isSendingMessage &&
      (force || pollsInFlight === 0);

    if (canPoll) {
      pollsInFlight += 1;
      const epochAtStart = epoch;
      try {
        const after = latestTimestamp(state.conversationHistory);
        const { data, error } = await pollConversation(siteId, {
          userId: userId as string,
          conversationId: conversationId as string,
          after,
        });
        if (epochAtStart === epoch) {
          applyPollResult(data, error);
        }
      } finally {
        pollsInFlight -= 1;
      }
    }
  }

  function applyPollResult(
    data: ChatWidgetPollResponse | undefined,
    error: string | undefined,
  ): void {
    if (error || !data) {
      pollFailureCount += 1;
      // Background polls fail quietly; the visitor is talking to the bot and
      // has no live connection to lose.
      if (pollMode === "live" && pollFailureCount >= MAX_POLL_FAILURES) {
        setState((prev) => ({ ...prev, liveActive: false }));
        emitError("Lost connection to live chat. Please refresh to reconnect.");
      }
    } else {
      pollFailureCount = 0;
      const hasNewTurns = data.turns.length > 0;
      const isLive = isLiveStatus(data.handoff_status);
      setState((prev) => ({
        ...prev,
        conversationHistory: hasNewTurns
          ? mergeTurns(prev.conversationHistory, data.turns)
          : prev.conversationHistory,
        hasRealConversation: hasNewTurns ? true : prev.hasRealConversation,
        ...handoffState(data.handoff_status),
        agentTyping: isLive && !hasNewTurns && Boolean(data.agent_typing),
      }));
    }
  }

  /** End of a send or form submit: clear the flag, then poll for anything the
   * send's response could not include (e.g. a takeover just after it). */
  const finishSending = (): void => {
    setState((prev) => ({ ...prev, isSendingMessage: false }));
    if (pollMode !== "off") {
      void runPoll(true);
    }
  };

  const session: ChatSession = {
    siteId,

    getState: () => state,

    subscribe: (listener) => {
      listeners.add(listener);
      syncPoller();
      return () => {
        listeners.delete(listener);
        syncPoller();
      };
    },

    addNoticeListener: (listener) => {
      noticeListeners.add(listener);
      // Drain so a later second mount does not replay notices the first
      // instance already showed.
      pendingNotices
        .splice(0)
        .forEach(({ kind, message }) => notify(listener, kind, message));
      return () => {
        noticeListeners.delete(listener);
      };
    },

    sendMessage: async (text, faqId, welcomeButtonId, quickReply) => {
      if (state.isSendingMessage) {
        return false;
      }

      const optimisticUserMessage: ChatWidgetTurn = {
        id: `user-${Date.now()}`,
        type: "user",
        text,
        timestamp: new Date().toISOString(),
      };

      epoch += 1;
      setState((prev) => ({
        ...prev,
        isSendingMessage: true,
        conversationHistory: [
          ...prev.conversationHistory,
          optimisticUserMessage,
        ],
      }));

      try {
        trackEvent("message_sent_customer", {
          quick_reply: quickReply === true,
        });

        const result = await sendMessage(
          siteId,
          text,
          undefined,
          faqId,
          welcomeButtonId,
        );

        if (result.error || !result.data) {
          setState((prev) => ({
            ...prev,
            conversationHistory: prev.conversationHistory.filter(
              (turn) => turn.id !== optimisticUserMessage.id,
            ),
          }));
          emitError(result.error || "Failed to send message");
          return false;
        }

        // The server's handoff status catches an operator takeover the widget
        // did not start, e.g. Pause chatbot in the inbox.
        setState((prev) => ({
          ...prev,
          conversationHistory: result.data!.conversation_history,
          forms: result.data!.forms ?? prev.forms,
          hasRealConversation: true,
          ...handoffState(result.data!.handoff_status),
        }));

        trackEvent("message_received_automated");

        return true;
      } catch (err) {
        setState((prev) => ({
          ...prev,
          conversationHistory: prev.conversationHistory.filter(
            (turn) => turn.id !== optimisticUserMessage.id,
          ),
        }));
        emitError(
          err instanceof Error ? err.message : "Unknown error occurred",
        );
        return false;
      } finally {
        finishSending();
      }
    },

    restartSession: async () => {
      // A send still out would answer into the new conversation and restore
      // the old one's id, so restarting waits until it has finished.
      if (!state.isSendingMessage) {
        try {
          trackEvent("chat_conversation_restarted");
          restartConversation(siteId);
          if (formCloseTimer) {
            clearTimeout(formCloseTimer);
            formCloseTimer = null;
          }
          // Queued notices describe the conversation being discarded.
          pendingNotices.length = 0;
          // Polls still out belong to the old conversation.
          epoch += 1;
          setState(() => ({
            conversationHistory: [],
            hasRealConversation: false,
            isSendingMessage: false,
            forms: seedForms,
            activeFormKey: null,
            handoffStatus: "bot",
            liveActive: false,
            agentTyping: false,
          }));
        } catch (err) {
          console.error("Error restarting session:", err);
        }
      }
    },

    openForm: (key) => {
      const form = state.forms.find((candidate) => candidate.key === key);
      if (!form) {
        emitError("This form is not available right now.");
        return;
      }
      setState((prev) => ({ ...prev, activeFormKey: key }));
    },

    closeForm: () => {
      setState((prev) => ({ ...prev, activeFormKey: null }));
    },

    updateFormValues: (patch) => {
      const activeFormKey = state.activeFormKey;
      if (!activeFormKey) {
        return;
      }
      setState((prev) => ({
        ...prev,
        forms: prev.forms.map((form) =>
          form.key === activeFormKey
            ? { ...form, values: { ...(form.values ?? {}), ...patch } }
            : form,
        ),
      }));
    },

    submitForm: async () => {
      const { activeFormKey, forms } = state;
      if (!activeFormKey) {
        return false;
      }
      const form = forms.find((candidate) => candidate.key === activeFormKey);
      if (!form) {
        return false;
      }

      epoch += 1;
      setState((prev) => ({ ...prev, isSendingMessage: true }));

      const formValues = form.values ?? {};

      try {
        const { data, error } = await sendMessage(siteId, "", {
          category: "form",
          key: form.key,
          ...(form.id ? { id: form.id } : {}),
          values: formValues,
        });

        if (error || !data) {
          emitError(error || "Failed to submit form");
          return false;
        }

        const enteredLiveChat = form.key === "handoff_live_chat";
        // Older backends omit `handoff_status`; assume the live-chat form worked.
        setState((prev) => ({
          ...prev,
          conversationHistory: data.conversation_history,
          forms: data.forms ?? prev.forms,
          hasRealConversation: true,
          ...handoffState(
            data.handoff_status ?? (enteredLiveChat ? "waiting" : undefined),
          ),
        }));

        if (form.key === "handoff_sms") {
          trackEvent("lead_captured_sms");
        } else if (enteredLiveChat) {
          trackEvent("lead_captured_webchat");
        } else if (form.key === "handoff_webhook") {
          trackEvent("lead_captured_webhook");
        }

        emitSuccess("Form submitted successfully!");

        if (formCloseTimer) {
          clearTimeout(formCloseTimer);
        }
        formCloseTimer = setTimeout(() => {
          formCloseTimer = null;
          setState((prev) => ({ ...prev, activeFormKey: null }));
        }, 2000);

        return true;
      } catch (err) {
        emitError(
          err instanceof Error ? err.message : "Unknown error occurred",
        );
        return false;
      } finally {
        finishSending();
      }
    },

    upsertTurnFeedback: async (turnId, patch) => {
      const userId = storage.getUserId(siteId);
      if (!userId) {
        emitError("User ID not found. Please try refreshing the page.");
        return false;
      }

      const prevHistory = state.conversationHistory;

      setState((prev) => ({
        ...prev,
        conversationHistory: prev.conversationHistory.map((turn) => {
          if (turn.id !== turnId || turn.type !== "system") {
            return turn;
          }
          const current: ChatWidgetTurnFeedback | undefined = turn.feedback;
          const next: ChatWidgetTurnFeedback = {
            id: current?.id || "",
            turnId: turn.id,
            rating: Object.prototype.hasOwnProperty.call(patch, "rating")
              ? patch.rating!
              : current?.rating ?? null,
            message: Object.prototype.hasOwnProperty.call(patch, "message")
              ? patch.message ?? null
              : current?.message ?? null,
            createdAt: current?.createdAt ?? null,
            createdBy: current?.createdBy ?? null,
            createdByName: current?.createdByName ?? null,
            createdByTomisId: current?.createdByTomisId ?? null,
            createdByEmail: current?.createdByEmail ?? null,
            resolved: current?.resolved ?? false,
            resolvedAt: current?.resolvedAt ?? null,
            resolvedBy: current?.resolvedBy ?? null,
            resolvedByName: current?.resolvedByName ?? null,
            resolvedByTomisId: current?.resolvedByTomisId ?? null,
          };

          const bothKeysProvided =
            Object.prototype.hasOwnProperty.call(patch, "rating") &&
            Object.prototype.hasOwnProperty.call(patch, "message");
          const bothEmpty =
            (next.rating === null || next.rating === undefined) &&
            (next.message === null ||
              next.message === undefined ||
              next.message === "");

          return {
            ...turn,
            feedback: bothKeysProvided && bothEmpty ? undefined : next,
          } as ChatWidgetTurn;
        }),
      }));

      const { data, error } = await upsertFeedback(siteId, {
        user_id: userId,
        turn_id: turnId,
        ...(Object.prototype.hasOwnProperty.call(patch, "rating")
          ? { rating: patch.rating ?? null }
          : {}),
        ...(Object.prototype.hasOwnProperty.call(patch, "message")
          ? { message: patch.message ?? null }
          : {}),
      });

      if (error) {
        setState((prev) => ({ ...prev, conversationHistory: prevHistory }));
        emitError(error);
        return false;
      }

      if (!data) {
        setState((prev) => ({ ...prev, conversationHistory: prevHistory }));
        emitError("Failed to save feedback");
        return false;
      }

      setState((prev) => ({
        ...prev,
        conversationHistory: prev.conversationHistory.map((turn) => {
          if (turn.id !== turnId || turn.type !== "system") {
            return turn;
          }
          if (data.deleted || !data.feedback) {
            return { ...turn, feedback: undefined } as ChatWidgetTurn;
          }
          return { ...turn, feedback: data.feedback } as ChatWidgetTurn;
        }),
      }));

      if (Object.prototype.hasOwnProperty.call(patch, "rating")) {
        if (patch.rating === "positive") {
          trackEvent("feedback_positive", { turn_id: turnId });
        } else if (patch.rating === "negative") {
          trackEvent("feedback_negative", { turn_id: turnId });
        }
      }
      if (Object.prototype.hasOwnProperty.call(patch, "message")) {
        if (patch.message && patch.message.trim().length > 0) {
          trackEvent("feedback_message", { turn_id: turnId });
        }
      }

      return true;
    },

    notifyTyping: () => {
      const operatorPresent = isLiveStatus(state.handoffStatus);
      const now = Date.now();
      const throttleElapsed = now - lastTypingSent > TYPING_THROTTLE_MS;

      if (operatorPresent && throttleElapsed) {
        const userId = storage.getUserId(siteId);
        const conversationId = storage.getConversationId(siteId);
        if (userId && conversationId) {
          lastTypingSent = now;
          void sendCustomerTyping(siteId, { userId, conversationId });
        }
      }
    },
  };

  return session;
}
